import { Prescripteur, useSociete, useTiersService } from '@europrocurement/l2d-domain';
import {
    AdressesAdresseJsonldAdresseCreate,
    AdressesAdresseJsonldAdresseUpdate,
    RelationSocieteTiersRelationSocieteTiersJsonldPrescripteurWriteRelationSociete,
    TiersPrescripteurJsonldPrescripteurUpdate,
} from '@europrocurement/l2d-domain/openApi/ApiTiers';
import {
    ADD_TIERS_CTX,
    Tiers,
    TiersAddress,
    tiersApiToStateTransformer,
    useTiersCtxDispatch,
} from '@europrocurement/l2d-modules/providers/TiersProvider';

const buildAddressPayload = (
    adresse: TiersAddress,
): AdressesAdresseJsonldAdresseCreate & AdressesAdresseJsonldAdresseUpdate => ({
    adresse1: adresse.adresse1,
    adresse2: adresse.adresse2 || undefined,
    adresse3: adresse.adresse3 || undefined,
    adresse4: adresse.adresse4 || undefined,
    codePostal: adresse.codePostal,
    ville: adresse.ville,
    codePays: adresse.codePays ?? 'FR',
    defaut: true,
});

const usePrescriberCRM = () => {
    const { prescribersApi, adressesApi } = useTiersService();
    const xIdSociete = useSociete();
    const tiersDispatch = useTiersCtxDispatch();

    const syncTiers = async (tiers: Tiers): Promise<Tiers> => {
        const currentRelation = tiers.relationsSocietes.find((r) => r.societeId === xIdSociete);

        await Promise.all([
            prescribersApi.updatePrescripteurTiersItem({
                id: String(tiers.id),
                tiersPrescripteurJsonldPrescripteurUpdate: {
                    raisonSociale: tiers.raisonSociale,
                    siren: tiers.siren,
                    tvaIntracommunautaire: tiers.tvaIntracommunautaire,
                    regimeTva: tiers.regimeTva,
                    demo: tiers.demo,
                    // personnePhysique is required by the API but absent from the Tiers context;
                    // prescripteurs are always personnes morales in this domain
                    personnePhysique: false,
                } as TiersPrescripteurJsonldPrescripteurUpdate,
                xIdSociete,
            }),
            tiers.adresse.id
                ? adressesApi.updateAdresseAdressesItem({
                      id: String(tiers.adresse.id),
                      adressesAdresseJsonldAdresseUpdate: buildAddressPayload(tiers.adresse),
                      xIdSociete,
                  })
                : adressesApi.createPrescripteurAdresseAdressesCollection({
                      tiers: tiers.id,
                      adressesAdresseJsonldAdresseCreate: buildAddressPayload(tiers.adresse),
                      xIdSociete,
                  }),
            prescribersApi.updateRelationSocietePrescripteurRelationSocieteTiersItem({
                tiers: tiers.id,
                societe: String(xIdSociete),
                relationSocieteTiersRelationSocieteTiersJsonldPrescripteurWriteRelationSociete: {
                    codeClient: tiers.codeComptable,
                    clientSuivi: currentRelation?.clientSuivi,
                    clientXl: currentRelation?.clientXl,
                    tiersNonFiable: currentRelation?.tiersNonFiable,
                    dafc: currentRelation?.dafc,
                    delaiPaiement: currentRelation?.delaiPaiement,
                    devisDetaille: currentRelation?.devisDetaille,
                    modePaiement: currentRelation?.modePaiement,
                    portefeuille: currentRelation?.portefeuille,
                    categorieSocioProfessionnelle: currentRelation?.categorieSocioProfessionnelle,
                    emailSousclientObligatoire: currentRelation?.emailSousclientObligatoire,
                    logoFormalite: currentRelation?.logoFormalite,
                    partenaireFormalite: currentRelation?.partenaireFormalite,
                } as RelationSocieteTiersRelationSocieteTiersJsonldPrescripteurWriteRelationSociete,
                xIdSociete,
            }),
        ]);

        // Re-fetch to get consistent state with all updates (tiers fields + address)
        const refreshResponse = await prescribersApi.getTiersTiersItem({
            id: String(tiers.id),
            xIdSociete,
        });

        const updated = tiersApiToStateTransformer(
            refreshResponse.data as Prescripteur,
            xIdSociete,
        );

        if (tiersDispatch) {
            tiersDispatch({ type: ADD_TIERS_CTX, payload: updated });
        }

        return updated;
    };

    return { syncTiers };
};

export default usePrescriberCRM;
